/**
 * Vocabulary: readers see this feature as "owner reviews"; the code, routes, DealHunt paths
 * (`/api/owner/*`) and analytics events keep the historical "owner note" name on purpose — do not
 * rename them. Every string below that a reader sees says "owner review" (since 2026-10-02).
 *
 * Owner-notes copy and the pure decisions behind it, for every surface that asks for, shows or
 * confirms a note (PDP rail and Owners section, category band and own tile, My gear):
 *
 *  - the ask — `BAND_CTA`, `ASK_COPY`, and what a reader learns BEFORE pressing the button
 *    (review of 2026-09-05, proposals 1–3): where the note lands and who is there already
 *    (payoff), whether it goes up now or waits for a person (timing), what the form holds (cost);
 *  - owner counts behind those lines (`ownersFrom*`, `ownersWithMine`);
 *  - the author's status words (`OWN_STATUS_LABEL`) and the result state's lines;
 *  - which shelf row is this page's note (`shelfItemMatches`, #110), whether a claim published
 *    it (`claimPublishedThisPage`, #120), whether signing in would (`heldClaimOffered`, #121), and
 *    whether a live anonymous note should be linked to keep it (`keepClaimOffered`);
 *  - the hero's ask (`railAskMode`, `READER_ASK`), the product-page anchors (`OWNERS_ANCHOR`,
 *    `OWNERS_SECTION_ID`, `noteAnchorId` / `noteDeepLink`), the note's small phrases
 *    (`noteOwnedLine`, `buyAgainMark`) and the casing pair (`lowerFirst` / `capitalize`).
 *
 * Pure, unit-tested; components only render it.
 */
import {
  OWNER_HELD_REVIEW_MESSAGE,
  type OwnerCardNote,
  type OwnerCardOwners,
  type OwnerClaimResponse,
  type OwnerNoteOwn,
  type OwnerNoteStatus,
  type OwnerNoteViews,
  type OwnersEnrichment,
  type OwnerNotesListResponse,
} from 'lib/dealhunt/types'
import type { OwnNoteSignal } from './bus'
import { VERIFY_PATH } from './hints'
import { milestonePromise } from './views'

/** "Under a month" → "under a month"; "1–6 months" stays. For labels spliced mid-sentence. */
export function lowerFirst(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/** "better" → "Better". For a phrase that opens a line or a chip. */
export function capitalize(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** The ask's button, on every surface (rail, Owners section, category tile). */
export const BAND_CTA = 'Leave an owner review'
/** The gated PDP asks' button when this tab holds a started band draft (client-only, after
 *  mount — `useBandDraftWaiting`); the band restores it on open. */
export const DRAFT_WAITING_CTA = 'Finish your owner review'
/**
 * "four quick reads" here, in ASK_COPY, OwnerExample and /how-we-verify-owners assumes every
 * category question set has four reads. Decided 2026-09-13: the copy stays; DealHunt question
 * sets do not change their read count without updating these strings in the same change.
 */
export const FORM_COST_LINE = 'Three questions, four quick reads, one sentence. Photo optional.'
/**
 * The ask, everywhere it is made (PDP rail, Owners section, the declared tile's cost line):
 * one vocabulary. It names the form's cost and never a duration — publishing may wait on
 * sign-in, the bot check or a moderator, and "30 seconds" on a page that explains how owners
 * are verified reads as a promise we cannot keep (band constraint, 2026-09-05; the rail and
 * section caught up 2026-09-09). To promise a duration again, change it here only.
 */
export const ASK_COPY = {
  title: (productName: string): string => `Own the ${productName}?`,
  /** PDP rail, under the scores card. */
  rail: (ownerCount: number): string => {
    if (ownerCount > 0) {
      return `${ownerCount} owner${
        ownerCount === 1 ? ' has' : 's have'
      } written in. Add yours — three answers, four quick reads.`
    }
    return 'Be the first owner to write in — three answers, four quick reads, shown under a pseudonym.'
  },
  /** Owners section, before the notes. */
  section:
    'Three answers, four quick reads, one sentence nobody tells you before buying. Shown under a pseudonym.',
} as const

/** The sign-in gate's one ask sentence (`lib/owner-notes/signin-gate.ts`). Reader-neutral and
 *  method-free: it sits in edge-cached HTML, read by signed-in readers too (for whom it is still
 *  true), and email availability is only known in the browser. */
export const SIGN_IN_NEEDED = 'Sign-in needed to publish.'

/** The undeclared category strip (one slim strip, 2026-10-17; the question link and the sign-in
 *  fine print went 2026-10-27): one row on desktop — the lead, the search — and one tap target on
 *  a phone, the search under it once "Add" is tapped. Noun-neutral ("the one you own", never "the pair"): the strip sits on speaker
 *  and soundbar pages too. */
export const CATEGORY_STRIP = {
  lead: (noun: string): string => `Own ${noun}?`,
  body: 'Add yours and every card compares with it.',
  placeholder: 'Which one do you own?',
  inputLabel: 'Which product do you own?',
  addTap: '+ Add the one you own',
} as const

/** `ASK_COPY` on a gated surface (the ask's handoff, `signInGateFor`): the same asks, plus the
 *  requirement — told at the ask, never first at Publish. */
export const GATED_ASK_COPY = {
  rail: (ownerCount: number): string => `${ASK_COPY.rail(ownerCount)} ${SIGN_IN_NEEDED}`,
  section: `${ASK_COPY.section} ${SIGN_IN_NEEDED}`,
} as const

/**
 * The ask under the PDP note reader (`OwnerNotesReader`): the form's cost, then the fine print.
 * Its own string — it was ASK_COPY.section regex-stripped of its last sentence, which a reword
 * of the section would have broken silently. No duration, like every other ask (audit
 * 2026-09-25 D3, settled 2026-10-07: "About 2 minutes" dropped).
 */
export const READER_ASK = {
  title: 'Own it? Your review joins these',
  text: 'Three answers, four quick reads, one sentence nobody tells you before buying · No account needed · Photo optional',
} as const

/** `READER_ASK` on a gated surface: the same shape, "No account needed" becomes the requirement. */
export const READER_ASK_GATED = {
  title: READER_ASK.title,
  text: 'Three answers, four quick reads, one sentence nobody tells you before buying · Sign-in needed to publish · Photo optional',
} as const

/**
 * Pure decision, unit-tested: whether an owner-note ask shows — the PDP Owners section and the
 * category tile alike (the rail follows the section's answer, `railAskMode`).
 * No Turnstile site key = publishing cannot succeed (DealHunt refuses the token), so the ask
 * is not made at all rather than failing at the last click; same value on SSR and client
 * (NEXT_PUBLIC), so hydration agrees. The server HTML is edge-cached and cannot know the
 * reader, so it always asks. Once hydrated, a browser with a device cookie waits for its own
 * note (`ownPending` = `useOwnNote().pending`) — otherwise an author sees the ask, then their
 * note replace it.
 */
export function ownerAskVisible(input: {
  configured: boolean
  hydrated: boolean
  hasOwnNote: boolean
  bandOpen: boolean
  ownPending: boolean
}): boolean {
  if (!input.configured) {
    return false
  }
  if (!input.hydrated) {
    return true
  }
  return !input.hasOwnNote && !input.bandOpen && !input.ownPending
}

/**
 * Pure, unit-tested: what the hero's ask shows. The Owners section publishes the reader's
 * own-note state (`publishOwnNote`); until it does, the server-rendered ask stands (the PDP HTML
 * is edge-cached and cannot know the reader). An author is never asked again — the ask points at
 * their note (decided 2026-09-13). While `/me` is pending it shows nothing rather than an ask
 * that turns into "See your review" — the same rule as `ownerAskVisible`.
 */
export function railAskMode(signal: OwnNoteSignal): 'ask' | 'pending' | 'author' {
  if (signal.hasNote) {
    return 'author'
  }
  return signal.pending ? 'pending' : 'ask'
}

export const EXAMPLE_TOGGLE = {
  closed: 'See what it looks like on the product page',
  open: 'Hide the example',
}

/** Owner counts for the declared product, whichever surface they came from. */
export interface OwnersSummary {
  count: number
  /** Null when only the count is known (a partial page of notes). */
  buyAgain: { yes: number; total: number } | null
}

export function ownersFromCard(owners: OwnerCardOwners | null | undefined): OwnersSummary | null {
  if (!owners) {
    return null
  }
  return { count: owners.count, buyAgain: owners.buy_again }
}

export function ownersFromEnrichment(
  owners: OwnersEnrichment | null | undefined
): OwnersSummary | null {
  if (!owners || owners.published_count <= 0) {
    return null
  }
  const agg = owners.aggregate
  return {
    count: owners.published_count,
    buyAgain: agg ? { yes: agg.buy_again_yes, total: agg.published_count } : null,
  }
}

/** From the public list: the count is exact; buy-again only when the page holds every note. */
export function ownersFromList(
  list: OwnerNotesListResponse | null | undefined
): OwnersSummary | null {
  if (!list || list.published_count <= 0) {
    return null
  }
  const complete = list.notes.length >= list.published_count
  return {
    count: list.published_count,
    buyAgain: complete
      ? {
          yes: list.notes.filter(n => n.answers.buy_again === true).length,
          total: list.notes.length,
        }
      : null,
  }
}

export interface PayoffLine {
  chips: string[]
  tail: string
}

/** Proposal 1: the destination and the community, before the click. */
export function payoffLine(owners: OwnersSummary | null): PayoffLine {
  if (!owners || owners.count <= 0) {
    return {
      chips: ['No owner reviews yet'],
      tail: 'Yours would be the first on its product page.',
    }
  }
  const chips = [`${owners.count} owner${owners.count === 1 ? '' : 's'} on its product page`]
  if (owners.buyAgain && owners.buyAgain.total > 0) {
    chips.push(`${owners.buyAgain.yes} of ${owners.buyAgain.total} would buy again`)
  }
  return { chips, tail: 'Your reads join those numbers.' }
}

/** The grid tile's headline (2026-09-08, proposal C): the ask is a card among the cards. */
export function tileHeadline(owners: OwnersSummary | null): string {
  return !owners || owners.count <= 0 ? 'Be the first owner to weigh in' : 'Add your read to theirs'
}

/**
 * Under the band's heading when the reference strip opened it — a guide (Guillaume, 2026-09-17)
 * and a category page (2026-09-18, the same strip): there the form opens on a Yes /
 * No tap, a screen away from any sentence that said what a note is, so the band makes the case
 * itself — in ONE line. A longer version (a pitch paragraph, a pill repeating the tapped answer,
 * a sentence on where the note shows) was tried the same day and cut: four blocks of text above
 * a form is a wall (his word: cognitive overload). What they said is still said where it is
 * needed — the tapped answer shows chosen at step 2, and `bandTiming` says where the note goes
 * and how it is checked at the Publish step.
 *
 * Not "We tested it": the strip asks about whatever the reader declared, which may be a product
 * the lab never saw. No review length and no duration for the form (see `ASK_COPY`).
 */
export const BAND_INTRO = {
  hook: 'We test products. You have lived with yours.',
  /** Was "what no review can" — reworded 2026-10-02 when the feature itself became "owner reviews". */
  pitch: 'Tell the next buyer what no test can.',
  link: { label: 'How we verify owners', href: `${VERIFY_PATH}#checks` },
} as const

// ── after a note exists (design 2026-09-08, docs/owner-notes-after-publish.md) ───────────

/** The Owners section's deep-link id on the product page — the one DealHunt's emails use. */
export const OWNERS_ANCHOR = 'owners'
/**
 * The page nav's id for the same section (`ProductPageTemplate` wraps the section in it; the
 * hero's links jump to it). Two ids on purpose — Constraint 4095b005: "`#owners` is the
 * deep-link id DealHunt's emails use (notifications.ts) — keep it on the Owners section;
 * `sec-owners` is the page nav's, not a substitute."
 */
export const OWNERS_SECTION_ID = 'sec-owners'
export function ownersDeepLink(productHref: string): string {
  return `${productHref}#${OWNERS_ANCHOR}`
}

/** One public note's card id on the product page (`OwnerNoteCard`) — "Read the full owner review" lands there. */
export function noteAnchorId(noteId: string): string {
  return `note-${noteId}`
}
export function noteDeepLink(productHref: string, noteId: string): string {
  return `${productHref}#${noteAnchorId(noteId)}`
}

/** "Owned over 2 years" from DealHunt's label ("Over 2 years"); nothing without one (#158). */
export function noteOwnedLine(note: Pick<OwnerCardNote, 'ownership_length'>): string | null {
  const label = note.ownership_length?.label
  if (!label) {
    return null
  }
  return `Owned ${lowerFirst(label)}`
}

/** The card's own buy-again mark, not a chip (design 2a) — the wall card and the reader's slide. */
export function buyAgainMark(buyAgain: boolean): string {
  return buyAgain ? 'Would buy again' : 'Would not buy again'
}

/**
 * My gear's Notifications block, where readers manage owner emails. Every owner email's footer
 * links to `/profile#owner-emails` (flowengine `settings_url`, "Change what you receive at …").
 */
export const OWNER_EMAILS_ANCHOR = 'owner-emails'

/** Pure, unit-tested: `/profile` opens My gear for `?tab=gear` and for the email footer's hash. */
export function profileOpensGear(queryTab: unknown, hash: string): boolean {
  return queryTab === 'gear' || hash === `#${OWNER_EMAILS_ANCHOR}`
}

/**
 * The email settings My gear offers (#95): `email_note_updates` (the check results on a reader's
 * notes and photos, and the recap), `email_view_milestones` (#142) and, since community
 * discussions (phase 3), `email_questions` — a question about gear the reader reviewed, an answer
 * to their own question. `accepts_questions` is not in My gear: since 2026-10-28 it is its
 * own switch on the hub's Settings tab (`ACCEPTS_QUESTIONS_COPY`, `SettingsView`). Since
 * category questions (2026-10-17) both say "gear you own": a question reaches the owners of a
 * product they reviewed or declared, or of its category. No durations, no commerce words.
 */
export const EMAIL_SETTINGS_COPY = {
  label: 'Email me about my reviews',
  detail: (emailMasked: string | null): string =>
    `${
      emailMasked ? `Sent to ${emailMasked} — ` : ''
    }when a review or photo has been checked, plus the occasional recap. Never marketing.`,
  offLine: 'Emails are off. Every update still shows up here.',
  saveFailed: "We couldn't save that just now — try again.",
  /** The second switch (#142): reader milestones, its own choice. */
  milestonesLabel: 'Email me when my reviews reach reader milestones',
  milestonesDetail:
    'Once a review passes 100, 500 or 1,000 readers — at most one email a week per review.',
  milestonesOffLine: 'Milestone emails are off. They still show up here.',
  /** The questions switch (community discussions, #95 `email_questions`). */
  questionsLabel: 'Email me when someone asks about gear I own, or answers my question',
  questionsDetail:
    'Questions that link a product you reviewed or declared, or its category, and answers to the questions you ask.',
  questionsOffLine: 'Question emails are off. They still show up in your notifications.',
} as const

/** The hub's `accepts_questions` switch (Settings tab): bell and email for questions. */
export const ACCEPTS_QUESTIONS_COPY = {
  label: 'Let readers ask you about gear you own',
  detail:
    'Questions on products you reviewed or declared, and on their category. Off, you are not told about new questions, on site or by email.',
} as const

/**
 * Said wherever a reader is asked to sign in for their owner reviews: signing in now also
 * creates a public member page, so the ask says so and names the switch.
 */
export const SIGN_IN_PUBLIC_PAGE_LINE =
  'Signing in also gives you a public member page under your owner name — the gear you reviewed and your badges. You can hide it any time in Settings.'

export const UNDECLARE_LABEL = 'Not yours? Change'
/** The strip's one-tap way out (2026-10-30): drop the declared pair without picking another.
 *  "Change" clears and opens the picker; this only clears. */
export const CLEAR_DECLARED = {
  label: 'Clear',
  aria: (name: string): string => `Stop comparing with your ${name}`,
} as const
/** Once a note exists the pick is no longer in doubt — the button only switches the baseline. */
export const SWITCH_LABEL = 'Own another? Switch'
export const SEE_ON_PAGE = 'See it on the product page'
/** The PDP rail once the reader has a note on this product (decided 2026-09-13): never a second ask. */
export const RAIL_AUTHOR = { title: 'Your owner review', cta: 'See your review' } as const
/** The PDP HTML is shared-cached 15 min; the author's own card there is fetched live. */
export const RESULT_TIMING_LINE = 'You see it there right away; other readers within 15 minutes.'

/**
 * The long check (2026-09-20). The publish request holds the guard judge and, once DealHunt's
 * `OWNER_COACH_ENABLED` is on, a SECOND model call (`coachOwnerNote`) — so the wait roughly
 * doubles and `STILL_CHECKING_AFTER_MS` stops being an exception and becomes the normal
 * terminal state. SG cannot see which call is running (it is one request), so this appears on
 * elapsed time alone and must be TRUE with the coach flag off as well.
 *
 * It buys the reader an EXIT, not advice: the note is already with DealHunt, leaving cannot
 * lose it, and every outcome is recoverable from My gear — the verdict on the own-note card,
 * and (ledger #151) the coaching too, the clue on that card and the peer line on the return
 * card. Saying this is only honest BECAUSE those two surfaces exist.
 *
 * Deliberately NOT a writing tip. By now the sentence is sent and cannot be changed, so a
 * craft hint here lands exactly too late to act on — and it would sit seconds above the
 * server's own per-note clue, which is the one invitation the author is meant to read
 * (ledger #48, directive 1042b402: the coaching sentence is never ours to write).
 */
export const LONG_CHECK_ESCAPE_LINE =
  'Your review is sent — you can leave this page. The result will be waiting in My gear.'

/**
 * Held for a moderator, not signed in (2026-09-11): the note first, the sign-in after, as
 * an option. The old result was a sign-in box and "Maybe later" — after three steps it read
 * as an email grab, with no sight of what the reader had written or where it lives.
 */
export function heldResultLead(
  handle: string | null | undefined,
  /** An edit on a LIVE note (#129): the earlier version stays public — never "only you see it". */
  liveEdit = false
): string {
  if (liveEdit) {
    return 'Your edit is saved. A moderator reads it first — until then, readers still see the earlier version.'
  }
  const who = handle ? ` under @${handle}` : ''
  return `Your review is saved${who}. A moderator reads it before it goes public — until then, only you can see it.`
}
export const HELD_CLAIM_LEAD = 'Want it public now? Sign in and it skips the moderator.'

/**
 * Step 3, before Publish, for a reader with no session and no linked device (2026-10-06): the
 * one sign-in line in the form. It only opens the dialog — the link still happens at Publish
 * with the identity row's consent copy (#81). A different, pre-result message from
 * `HELD_CLAIM_LEAD` (that one stays behind `heldClaimOffered`). No figures and no durations: the
 * band has no points data (the community form's `signInLine` does, and prints its own panel).
 */
export const PRE_PUBLISH_SIGN_IN = {
  lead: 'Photos and points need an account. Your draft stays here.',
  button: 'Sign in',
} as const

/**
 * The sign-in gate inside the band (`lib/owner-notes/signin-gate.ts`, gated creates only): step 1
 * says it once, and at step 3 `lead` takes `PRE_PUBLISH_SIGN_IN`'s slot and `button` replaces
 * "Publish" while signed out — it only opens the dialog; the reader clicks Publish again after
 * (the link still happens at Publish, #81). Never a sell of speed or a moderator wait
 * (aca4da79), never an anonymous way out.
 */
export const SIGN_IN_GATE = {
  /** No sign-in method is named here (2026-10-17, Guillaume): the dialog shows them itself. */
  step1: "You'll sign in to publish at the last step. What you write stays here.",
  lead: 'Sign in to publish. Your review stays right here while you do.',
  /** The lead for a browser whose device is already linked (`me.profile.claimed`) but has no
   *  session: the gate is a live session (2026-10-17), so it names the handle and asks to confirm. */
  linkedLead: (handle: string): string => `Publishes as @${handle}. Sign in to confirm it's you.`,
  /** The linked lead inside the band's own preview card (2026-10-17): the card's byline already
   *  names the handle, so this placement drops "Publishes as @x." The composer (`hostPanels`)
   *  keeps `linkedLead` in its standalone panel. */
  confirmLead: "Sign in to confirm it's you.",
  button: 'Sign in to publish',
  /** Only when email sign-in is offered: the link may open in a new tab with an empty band. */
  linkHint: 'Signing in from the email link? Come back to this tab to publish.',
} as const

/** Step 3's fine print (2026-10-07): anonymous reviews publish on the automatic check alone —
 *  no moderator step and no duration (3a6ce92b). */
export const STEP3_FINE =
  'Owner reviews are checked automatically and published as soon as they pass. Your pseudonym is shown; nothing else about you is.'

/**
 * The buy-again answer the ask already took (the strip's / guide row's Yes / No, `seedBuyAgain`):
 * said once in step 1, and step 2 does not ask it again unless the reader taps "change".
 */
export function buyAgainAck(buyAgain: boolean): string {
  return buyAgain ? "You'd buy it again." : "You wouldn't buy it again."
}
export const BUY_AGAIN_CHANGE = 'change'

// ── the accepted result (2026-10-07) ─────────────────────────────────────────

/** The accept result's primary link: the review's own page in the community (its thread). */
export const SEE_IN_COMMUNITY = 'See it in the community →'

/**
 * A signed-out (unlinked) author's accepted review: what signing in adds, as a list. Every
 * figure is DealHunt's, from the publish answer's `community` block — the points this publish
 * earned (`points_awarded`, which the profile keeps when it is claimed) and the Early Owner spots
 * left. The photo bonus is NOT on the publish answer (the band has no points table), so the photo
 * item carries no number; reader milestones are SoundGuys' mirror of a DealHunt constant, not a
 * payload, so the bell item names none. Replies are not in it: `reply_received` is email + My
 * gear, never the bell (DealHunt OWNER_BELL_KINDS_DEFAULT).
 */
export const MAKE_IT_COUNT = {
  title: 'Make it count',
  lead: 'Sign in and this review gives you:',
  fine: 'Your pseudonym stays. Your email is never shown.',
  photos: 'Photos on this review',
  bell: 'A bell when it reaches new readers',
} as const

/** The sign-in dialog when the gate opens it (`showLogIn({ copy })`): a review, not comments.
 *  No speed, no moderator (aca4da79). Google and email in the dialog are unchanged. */
export const GATE_LOGIN_DIALOG = {
  title: 'Sign in to publish your review',
  subtitle: MAKE_IT_COUNT.fine,
} as const

/** Which accept-result panel an author gets: `make_it_count` when the review is not linked to an
 *  account (and the account can link — `retry: 'none'` = no verified email, nothing to offer);
 *  `ask` = linked: the standing lines and the seeded question for the other owners. */
export function acceptPanel(
  linked: boolean,
  linkRetry: 'link' | 'sign_in' | 'none' | null | undefined
): 'make_it_count' | 'ask' | 'none' {
  if (linked) {
    return 'ask'
  }
  return linkRetry === 'none' ? 'none' : 'make_it_count'
}

export function makeItCountItems(
  standing: { points_awarded?: number; founding_spots_left?: number } | null | undefined
): string[] {
  const items: string[] = []
  const points = standing?.points_awarded ?? 0
  if (points > 0) {
    items.push(`+${points.toLocaleString('en-US')} points on the community leaderboard`)
  }
  const spots = standing?.founding_spots_left ?? 0
  if (spots > 0) {
    items.push(`An Early Owner badge (${spots.toLocaleString('en-US')} spots left)`)
  }
  items.push(MAKE_IT_COUNT.photos, MAKE_IT_COUNT.bell)
  return items
}

/**
 * The seeded "ask the other owners" question (2026-10-07): one template per read id in
 * DealHunt's question sets (`defaultQuestionSet.ts`, `categoryQuestionSets.ts`). Picked from
 * the writer's own ANSWERS only — never from the sentence they wrote.
 */
export const READ_QUESTION_TEMPLATES: Readonly<Record<string, string>> = {
  battery: 'How long does the {product} battery really last for you?',
  anc: 'Does the noise cancelling on the {product} hold up for you in the real world?',
  comfort: 'How long can you wear the {product} before it gets uncomfortable?',
  mic: 'How does the {product} mic sound to the people you talk to?',
  build: 'How has the {product} held up for you after months of use?',
  connection: 'Does the {product} wireless connection drop or lag for you?',
  isolation: 'How much outside noise does the {product} block for you?',
  cable: 'How does the {product} cable hold up in daily use for you?',
  loudness: 'How does the {product} sound for you when you turn it up?',
  positional: 'Can you tell where sounds come from with the {product} in games?',
}
export const READ_QUESTION_FALLBACK =
  'What should a buyer know about the {product} that reviews miss?'

export const ASK_OWNERS = {
  title: 'Ask the other owners something',
  button: 'Ask this →',
  fine: 'Signed-in owners of this product hear about your question.',
} as const

/**
 * The seed: the FIRST read the writer marked "worse", in the question set's order (not tap
 * order), through its template; no "worse" read, or a read with no template → the fallback.
 */
export function askOwnersSeed(
  productName: string,
  reads: Readonly<Record<string, string | null | undefined>>,
  order: ReadonlyArray<{ id: string }> | null
): string {
  const ids = order ? order.map(r => r.id) : Object.keys(reads)
  const worse = ids.find(id => reads[id] === 'worse')
  const template = (worse && READ_QUESTION_TEMPLATES[worse]) || READ_QUESTION_FALLBACK
  return template.replace('{product}', productName)
}

/** Whether step 3 shows `PRE_PUBLISH_SIGN_IN`: the session check has answered (no flash for a
 *  signed-in reader), nobody is signed in, this browser's device is not linked, the host
 *  does not print its own sign-in panel (`hostPanels`, the community form), and the publish
 *  counts — an edit of a published review earns no points, so the line would promise what does
 *  not come (the community panel's `counts` rule). */
export function prePublishSignInShown(input: {
  hostPanels: boolean
  counts: boolean
  resolved: boolean
  signedIn: boolean
  claimed: boolean
}): boolean {
  return !input.hostPanels && input.counts && input.resolved && !input.signedIn && !input.claimed
}

/**
 * An anonymous author's LIVE note (2026-09-14). Once a moderator approved it nothing invited a
 * sign-in (`heldClaimOffered` is the held note's "publish now" button), yet only this browser's
 * device cookie holds the authorship: cleared cookies, another device, the cookie's 400 days or a
 * Logout click (which forgets an unclaimed device too, 2026-09-13) and the note stays up with no
 * one able to edit or delete it. Never beside the review message — that note already carries the
 * sign-in that publishes its edit (#129).
 */
export function keepClaimOffered(
  note: { status: OwnerNoteStatus; reader_message: string | null },
  claimed: boolean
): boolean {
  return (
    !claimed && note.status === 'published' && note.reader_message !== OWNER_HELD_REVIEW_MESSAGE
  )
}

export const KEEP_CLAIM_LEAD =
  'Only this browser knows this review is yours. Sign in once to keep it on every device.'

/** With a count (#138) the keep line names the gain, not only the loss. */
export function keepClaimLead(views: OwnerNoteViews | null | undefined): string {
  const promise = views ? milestonePromise(views) : null
  return promise ? `${KEEP_CLAIM_LEAD} ${promise}` : KEEP_CLAIM_LEAD
}

/** The claim CTA's fine print on a live note: the note does not change, but a merge renames it (#24). */
export const KEEP_CLAIM_FINE = `Your review stays up as it is. If your account already has an owner name, the review moves under it. Your email only keeps your reviews together and tells you about them. ${SIGN_IN_PUBLIC_PAGE_LINE}`

/** The prompt after a sign-in on a browser holding anonymous notes (`OwnerKeepPrompt`). */
export function keepPromptCopy(count: number, handle: string): { title: string; lead: string } {
  const one = count === 1
  return {
    title: one ? 'Keep your owner review' : `Keep your ${count} owner reviews`,
    lead: `You wrote ${
      one ? 'a review' : `${count} reviews`
    } as @${handle} before signing in. Link ${one ? 'it' : 'them'} to your account so ${
      one ? 'it stays' : 'they stay'
    } yours after you log out or switch device.`,
  }
}

/**
 * Signing in with an account that already has an owner name folds this browser's generated
 * pseudonym into it (DealHunt `mergeProfiles`, #24): the note now shows under the account's
 * name. Say so, or the reader sees "@guillaume" in one line and "@sharp_aspen_70" in the next.
 */
export function handleChangeLine(
  from: string | null | undefined,
  to: string | null | undefined
): string | null {
  if (!from || !to || from === to) {
    return null
  }
  return `Your review now shows under your account's name, @${to}, instead of @${from} — and so will anything you write from this browser.`
}

/** Under the reopened typeahead: switching the baseline never touches the note. */
export function switchLine(productName: string): string {
  return `Your review of the ${productName} stays up. Pick another product you own to compare the cards against it.`
}

/**
 * The reference strip's second line on a category page: what declaring changed HERE (a guide
 * says its charts, `GUIDE_COPY.strip.body`). The undeclared band's own promise, kept.
 */
export function categoryStripBody(categoryName: string, categoryNoun?: string): string {
  return `Every ${categoryNounOf(
    categoryName,
    categoryNoun
  )} card shows how it compares with yours.`
}

/** The category noun in running copy: DealHunt's `summary.noun` (backend-owned, keeps
 *  acronyms — "AI translation earbuds"), else the node name lowercased. */
export function categoryNounOf(categoryName: string, categoryNoun?: string): string {
  return categoryNoun || categoryName.toLowerCase()
}

/**
 * The owners line on the reader's own product card once their note exists (2026-09-11).
 * The grid's counts come from DealHunt's index through an edge-cached page, so they lag a
 * publish — on this page view (`publishedHere`: fold the note in once) and after a reload
 * too, until the index catches up. So a live own note is also a floor: never "no owner
 * notes yet" beside the reader's own published note. Counts above zero are trusted as
 * already holding it (the index knows other notes, so it has been rebuilt since).
 */
export function ownersWithMine(
  owners: OwnersSummary | null,
  note: { status: OwnerNoteStatus; answers: { buy_again: boolean } } | null,
  publishedHere: boolean
): OwnersSummary | null {
  if (!note || note.status !== 'published') {
    return owners
  }
  if (!publishedHere && owners && owners.count > 0) {
    return owners
  }
  const count = (owners?.count ?? 0) + 1
  const prior = owners?.buyAgain ?? (owners && owners.count > 0 ? null : { yes: 0, total: 0 })
  return {
    count,
    buyAgain: prior
      ? { yes: prior.yes + (note.answers.buy_again ? 1 : 0), total: prior.total + 1 }
      : null,
  }
}

/** The author's status words — the own card's chip and the own product card's tile. */
export const OWN_STATUS_LABEL: Record<OwnerNoteStatus, string> = {
  checking: 'Checking',
  published: 'Published',
  rejected: 'Not published',
  held_retry: 'Saved — check pending',
  held_review: 'Waiting for a moderator',
  hidden: 'Hidden pending review',
  removed: 'Removed',
}

/** DealHunt's status columns are TEXT and readers tolerate unknown strings: a status this build
 *  does not know reads as pending, never as a raw column value. */
export function ownStatusLabel(status: string): string {
  return (OWN_STATUS_LABEL as Record<string, string>)[status] ?? 'Pending'
}

/**
 * Which author controls a note offers (#123). A report-hidden note (a moderator is reviewing) and
 * a moderator-removed one belong to the moderator: DealHunt refuses the edit and the delete with
 * 409 `note_locked` / `note_removed`, so neither button is shown. `removed` never reaches `/me`
 * (the shelf leaves it out); it is listed for a local patch or a future answer. A note being
 * checked cannot be edited (today's rule) but can be deleted. An unknown status is treated like
 * `checking`: nothing that opens the form, and a delete DealHunt answers for itself.
 */
export function ownNoteControls(status: string): {
  edit: boolean
  delete: boolean
} {
  if (status === 'hidden' || status === 'removed') {
    return { edit: false, delete: false }
  }
  if (status === 'checking' || !(status in OWN_STATUS_LABEL)) {
    return { edit: false, delete: true }
  }
  return { edit: true, delete: true }
}

/**
 * The author's Delete on their own review and its inline question — one wording wherever a review
 * can be deleted (the own card on My gear / the product page, the thread's author bar). Asked
 * inline, never `window.confirm`.
 */
export const OWN_DELETE_COPY = {
  button: 'Delete',
  question: 'Delete this review?',
  confirm: 'Yes, delete',
  keep: 'Keep it',
} as const

/**
 * After an instant publish: promise only the mail DealHunt sends for a linked profile — a later
 * moderation decision. (Review photos are public at publish: no photo verdict to wait for.) Null
 * when nothing is linked.
 */
export function resultEmailLine(args: { linkedEmail: string | null }): string | null {
  return args.linkedEmail
    ? `We email ${args.linkedEmail} if anything changes about the review.`
    : null
}

/** A linked note held for a moderator: the one event that mails when it goes live. */
export function heldEmailLine(linkedEmail: string | null): string | null {
  return linkedEmail ? `We email ${linkedEmail} when it goes live.` : null
}

/**
 * A stored declaration holds the id the reader picked (grid card / resolve node); the shelf
 * holds the canonical the note attached to, plus its aliases (#110). Match on either.
 */
export function shelfItemMatches(
  item: { node_id: string; node_ids?: string[] },
  nodeId: string
): boolean {
  return item.node_id === nodeId || (item.node_ids ?? []).includes(nodeId)
}

/**
 * Did this claim publish THIS page's note (#120)? A claim publishes the profile's `held_review`
 * notes the automatic check accepted (#64/#121); `published_node_ids` names them by `node_id`, the form `/me` shelf rows
 * carry. A page's node can be an alias of that canonical (#110), so the ids are matched against
 * the note's group — the page id, the note's own `node_id`, the shelf row's `node_ids` — never
 * `===` on the page id alone. Only a `held_review` note can have gone live through a claim.
 *
 * `true` / `false`: DealHunt said. `null`: DealHunt predates #120 (no ids) — patch nothing and
 * refetch `/me`. The profile-wide `published_now` never decides a page's state (audit
 * capture-1: a held note on product B flipped product A's rejected card to Published).
 */
export function claimPublishedThisPage(
  claim: Pick<OwnerClaimResponse, 'published_node_ids'>,
  note: { status: OwnerNoteStatus; node_id?: string } | null,
  pageNodeId: string,
  shelfRow: { node_id: string; node_ids?: string[] } | null = null
): boolean | null {
  const ids = claim.published_node_ids
  if (!ids) {
    return null
  }
  if (!note || note.status !== 'held_review') {
    return false
  }
  const group = new Set<string>([pageNodeId])
  if (note.node_id) {
    group.add(note.node_id)
  }
  if (shelfRow) {
    group.add(shelfRow.node_id)
    ;(shelfRow.node_ids ?? []).forEach(id => group.add(id))
  }
  return ids.some(id => group.has(id))
}

/**
 * May this own note offer "sign in to publish now" (#121)? DealHunt publishes on claim only the
 * held notes its judge accepted; appealed and out-of-resubmits notes wait for a moderator whatever
 * the author does. It says which through `reader_message`: `OWNER_HELD_REVIEW_MESSAGE` only when
 * signing in would publish, `OWNER_HELD_MODERATOR_MESSAGE` otherwise — so the button follows the
 * message, never the status alone.
 *
 * A held note with NO message keeps today's button: every DealHunt that sent none predates #121,
 * when a claim published every held note (and pre-#121 `/me` sent the review message to every
 * unclaimed author anyway). A claimed author is never offered it.
 */
export function heldClaimOffered(
  note: { status: OwnerNoteStatus; reader_message: string | null },
  claimed: boolean
): boolean {
  if (claimed || note.status !== 'held_review') {
    return false
  }
  return note.reader_message === null || note.reader_message === OWNER_HELD_REVIEW_MESSAGE
}

/** What the author's card says under a LIVE note (#126/#129) — see `liveNoteNotice`. */
export type LiveNoteNotice =
  | { kind: 'claim'; lead: string }
  | { kind: 'message'; text: string }
  | { kind: 'checking' }
  | null

/**
 * The line under the author's own live note. `/me` now explains a live note in two cases:
 *  - #126: the last edit was rejected and dropped ("Your last edit wasn't published, so the earlier
 *    version stays up. <reason>"), `has_pending_revision` false again → shown verbatim;
 *  - #129: an anonymous author's edit the judge accepted waits for approval
 *    (OWNER_HELD_REVIEW_MESSAGE), `has_pending_revision` true → the sign-in button, which publishes
 *    the edit — exactly the held-note rule (`heldClaimOffered`).
 * "Your edit is being checked" stays only for a pending edit with no message — which is also every
 * answer from a DealHunt before #126/#129, so old answers read as they do today. A claimed author
 * holding the review message signed in since `/me` answered (the claim published the edit, #129,
 * and `/me` is re-read — `claimNeedsReread`): nothing, rather than a stale button or sentence.
 * A hidden note's reader_message is ignored and its pending edit reads as today. Hiding a note writes
 * no message and DealHunt puts the edit messages on published notes only, so a hidden note's message
 * is just the latest in its history — often an old rejection reason, never about the edit.
 */
export function liveNoteNotice(
  note: { status: OwnerNoteStatus; reader_message: string | null; has_pending_revision: boolean },
  claimed: boolean
): LiveNoteNotice {
  if (note.status !== 'published' && note.status !== 'hidden') {
    return null
  }
  const message = note.status === 'published' ? note.reader_message : null
  if (message === OWNER_HELD_REVIEW_MESSAGE) {
    return claimed ? null : { kind: 'claim', lead: message }
  }
  if (message) {
    return { kind: 'message', text: message }
  }
  return note.has_pending_revision ? { kind: 'checking' } : null
}

/** The author's own sentences under a note — `OwnNoteCard` and the member hub's Gear rows. */
export const OWN_EDIT_CHECKING_LINE =
  'Your edit is being checked; the earlier version stays up until it passes.'
export const OWN_HELD_RETRY_LINE =
  "We couldn't check your review just now — it will be checked again automatically."
export const OWN_HIDDEN_LINE = 'Readers reported this review; a moderator is reviewing it.'

/**
 * The AI proofread (DealHunt, 2026-10-06): SoundGuys' AI may fix typos in a LIVE review. The
 * author's card says so under the text with an inline Undo (`OwnNoteCard`); readers see
 * "· lightly proofread" on the thread (`THREAD_PROOFREAD_*`, `ThreadReview`).
 */
export const OWN_PROOFREAD_LINE = 'Lightly proofread by AI for typos'
export const OWN_PROOFREAD_UNDO = 'Undo'
/** 409 that is not a moderator lock: DealHunt has nothing to undo (already undone, or edited). */
export const OWN_PROOFREAD_NOTHING_TO_UNDO =
  'There is nothing to undo — this review is already in your words.'
export const OWN_PROOFREAD_UNDO_FAILED = "We couldn't undo the proofread — try again."
export const THREAD_PROOFREAD_MARK = '· lightly proofread'
export const THREAD_PROOFREAD_TITLE =
  "Proofread for typos by SoundGuys' AI. The author can undo it."

/** Undo is an author write like Edit: offered on a proofread note whose controls allow an edit
 *  (`ownNoteControls`, with a #123 lock already folded in by the caller) — never on a hidden,
 *  removed or locked note. */
export function proofreadUndoOffered(
  note: { proofread?: boolean },
  controls: { edit: boolean }
): boolean {
  return note.proofread === true && controls.edit
}

/** The card's local answer to an Undo: the note DealHunt returned, shown while the host still
 *  holds the proofread text it replaced. */
export interface ProofreadUndo {
  id: string
  /** The proofread text the Undo was pressed on. */
  fromText: string
  to: OwnerNoteOwn
}

/**
 * DealHunt's undo answer as the own note. The contract says "the same own-note shape as the
 * PATCH": a `{ note: OwnerNoteOwn, … }` envelope or the bare own note are both read. Anything
 * else is null — the caller re-reads `/me` instead of guessing the author's words.
 */
export function noteAfterUndo(body: unknown, before: OwnerNoteOwn): OwnerNoteOwn | null {
  const isOwn = (v: unknown): v is OwnerNoteOwn =>
    typeof v === 'object' &&
    v !== null &&
    (v as { id?: unknown }).id === before.id &&
    typeof (v as { note?: unknown }).note === 'string'
  if (typeof body !== 'object' || body === null) {
    return null
  }
  const inner = (body as { note?: unknown }).note
  let found: OwnerNoteOwn | null = null
  if (isOwn(inner)) {
    found = inner
  } else if (isOwn(body)) {
    found = body
  }
  // The answer to an undo is the author's own words: never re-mark it if a field is missing.
  return found ? { ...before, ...found, proofread: found.proofread === true } : null
}

/** The note the card shows: the undo's answer while the host's note is still the proofread
 *  text it replaced; the host's note as soon as it moves on (a fresh `/me`, an edit). */
export function shownOwnNote(note: OwnerNoteOwn, undo: ProofreadUndo | null): OwnerNoteOwn {
  return undo && undo.id === note.id && undo.fromText === note.note ? undo.to : note
}

/** One line under the author's note: `alert` = DealHunt's reason (rejection, dropped edit). */
export interface OwnNoteLine {
  tone: 'alert' | 'fine'
  text: string
}

/**
 * What `OwnNoteCard` prints under a note, for a SIGNED-IN author (the member hub's Gear rows: the
 * hub's owner layer exists only for a claimed member, so the sign-in-publishes branch never
 * applies — `heldClaimOffered` is false). Same order and words as the card: the live notice
 * (`liveNoteNotice`), the rejection's `reader_message` in full, the held and hidden lines.
 */
export function ownNoteLines(note: {
  status: OwnerNoteStatus
  reader_message: string | null
  has_pending_revision: boolean
}): OwnNoteLine[] {
  const lines: OwnNoteLine[] = []
  const live = liveNoteNotice(note, true)
  if (live?.kind === 'checking') {
    lines.push({ tone: 'fine', text: OWN_EDIT_CHECKING_LINE })
  } else if (live?.kind === 'message') {
    lines.push({ tone: 'alert', text: live.text })
  }
  if (note.status === 'rejected' && note.reader_message) {
    lines.push({ tone: 'alert', text: note.reader_message })
  } else if (note.status === 'held_retry') {
    lines.push({ tone: 'fine', text: note.reader_message ?? OWN_HELD_RETRY_LINE })
  } else if (note.status === 'held_review' && note.reader_message) {
    lines.push({ tone: 'fine', text: note.reader_message })
  } else if (note.status === 'hidden') {
    lines.push({ tone: 'fine', text: OWN_HIDDEN_LINE })
  }
  return lines
}

// ── The best-list guide funnel (docs/owner-notes-guide-funnel-plan.md, WP2) ───────────────

/**
 * The guide's own words, appended — nothing above is edited by the funnel.
 *
 * The comparison is NOT the ask: the capture line always renders (it offers a comparison, not
 * a note), while the strip's and the rail's asks gate on `turnstileConfigured()` and decide
 * through `ownerAskVisible` like every other ask. The ask itself reuses `ASK_COPY` — one
 * vocabulary — so no duration is promised here either.
 */
export const GUIDE_COPY = {
  /** The banner at the top of the Quick Answer roster, for a reader who declared nothing
   *  (2026-09-16 design round; it was a dashed line under the roster). "Something", not "one"
   *  (Guillaume, 2026-10-17: "Already own one?" read as one of the picks, and the audience is
   *  anyone with a product the lab measured — headphones, earbuds, speakers). The body is what
   *  declaring gets the reader: the comparison; the owner review is asked for on the pick they
   *  own, once they are here. */
  line: {
    title: 'Already own something?',
    body: 'Anything we have measured, not just the picks. It becomes a second curve on every chart here.',
    cta: 'Pick what you own',
    note: 'Nothing to sign up for',
  },
  /** The roster row's strip: three chips and the measurements toggle (design 2026-09-17; the
   *  16th's lead + "Compare with mine" on the first row are gone — the banner above the roster
   *  is the one ask). Undeclared, a chip is the pick's value on its scale; declared, it is
   *  "vs yours". The toggle opens the pick's lab panel in the row. */
  roster: {
    vs: 'vs yours',
  },
  /**
   * The action row under every pick (2026-10-17): three fixed slots — Pin to compare,
   * Measurements, Owners — in that order, with the same words on a roster row, an in-depth
   * card and the phone sheet. A slot the pick cannot honour is greyed, never removed, so the
   * row never shifts. "See the measurements" / "…against yours" / "Hide the measurements" and
   * the in-depth strip's "Compare with the one you own" are gone: one label per action, and
   * declaring is the banner's alone.
   */
  actions: {
    /** The pin slot on the pick the reader owns: nothing to pin it against. */
    pinYours: 'Yours',
    /** The pin slot on a pick the lab has not measured. */
    pinUnmeasured: 'Not lab-tested',
    measurements: 'Measurements',
    /** Under "Measurements" once a product is declared: the panel draws theirs too. */
    measurementsVs: 'vs yours',
    owners: 'Owners',
    ownersNone: 'none yet',
    /** The muted Owners segment's `title` (panel and sheet) when the pick has no owner reviews
     *  (2026-10-19: muted, still clickable), and `ownersEmpty` the body it opens on. */
    ownersEmptyTip: 'No owner reviews yet',
    ownersEmpty: 'No owner reviews yet.',
    ownersCount: (count: number): string => `${count} review${count === 1 ? '' : 's'}`,
  },
  /** The desktop panel the row opens (2026-10-18): the pins' side panel, on this pick — its
   *  first segment; the other two are the sheet's Owners and "Compare · n of 3". */
  panel: {
    thisPick: 'This pick',
    /** The foot's compare tile, right of the pin (2026-10-30): it opens what the segment shows
     *  and names those products under its label. "This pick" / Owners: "Compare with yours"
     *  with a declared pair, else "Compare"; Compare: "Compare these n". */
    compareYours: 'Compare with yours',
    compare: 'Compare',
    compareThese: (n: number): string => `Compare these ${n}`,
    compareJoin: ' vs ',
  },
  /** The phone sheet the row opens (2026-10-17; three segments 2026-10-19): one sheet per pick —
   *  "This pick" (`panel.thisPick`), Owners, and "Compare · n of 3", the pinned comparison
   *  inside the sheet — and the pin and compare tiles in its foot. `compare` also labels the drawer's
   *  segment; the empty Owners body is `actions.ownersEmpty`. */
  sheet: {
    vs: (declaredName: string): string => `vs your ${declaredName}`,
    compare: (pinned: number, max: number): string => `Compare · ${pinned} of ${max}`,
    close: 'Close',
  },
  /** The Compare body (`LabCompareBody`, shared by the desktop panel and the phone sheet) with
   *  nothing pinned yet (2026-10-19). `emptyBody` takes how many more fit beside this pick
   *  (`maxPins - 1`) so the number follows the cap; `emptyTip` is the muted Compare segment's
   *  `title`. */
  compare: {
    emptyTitle: 'Nothing pinned yet',
    emptyBody: (more: number): string =>
      `Pin this pick to put its curve on the chart, then pin up to ${more} more from the list to read them together.`,
    emptyTip: 'Pin a pick to compare it here',
  },
  /** The chooser, once the line is tapped: the shelf, then the search, then the picks. */
  chooser: {
    title: 'What do you own?',
    shelf: 'Your gear',
    picks: 'It’s one of the picks',
    /** A pick DealHunt has not joined yet (`context: null`): named once, never tappable. */
    pickDisabled: 'Coming soon',
    searchLabel: 'Which product do you own?',
    searchPlaceholder: 'Search what you own…',
    /** Under the title: the search takes anything the lab measured, the chips are a shortcut. */
    any: 'Anything we have measured works — it does not have to be one of the picks.',
    searching: 'Searching…',
    nothing: (query: string): string => `Nothing matches “${query}”.`,
    close: 'Cancel',
  },
  /** The reference strip that replaces the line once a product is declared. */
  strip: {
    lead: (productName: string): string => `Compared with your ${productName}`,
    /** Under the lead (design 2026-09-17). */
    body: 'Every chart on this page overlays yours.',
    /**
     * In the ask's place on a guide (2026-10-17): the strip asks nothing itself. The one ask
     * sits on the pick the reader owns, so the strip points there — or, when what they own is
     * not a pick on this page, at its product page, where its owner reviews live.
     */
    seatLink: (ownerCount: number): string => {
      if (ownerCount <= 0) {
        return 'No owner has reviewed it yet. Be the first ›'
      }
      return `Add your read to the ${ownerCount} owner${ownerCount === 1 ? '' : 's'}’ ›`
    },
    elsewhere: 'Leave an owner review on its product page ›',
    /**
     * The ask, as the question itself (design 2026-09-17, option A: "ask the question instead
     * of naming the task"). It is the form's OWN buy-again question, word for word, with its
     * own two answers — the tap is that answer, given early, and opens the rest of the note.
     * The design's wording ("Would you recommend them…", a third "With caveats") is not the
     * question owners are asked nor an answer the contract stores, and "One tap. Your answer
     * publishes" is not what happens: a note publishes, after three questions and a sentence.
     */
    question: 'Would you buy it again?',
    answers: { yes: 'Yes', no: 'No' },
    /** `cost`: the line after the count — a handed-off strip passes `handoffHint` (the writer
     *  opens, possibly in a new tab), else the in-place band's line. */
    questionBody: (
      ownerCount: number,
      cost = 'Your answer opens your owner review: three questions, one sentence.'
    ): string => {
      if (ownerCount <= 0) {
        return `No owner has answered yet. ${cost}`
      }
      return `${ownerCount} owner${ownerCount === 1 ? ' has' : 's have'} answered. ${cost}`
    },
  },
  /** The pick the reader owns: its tag, on its roster row and its in-depth block ("it", not
   *  "these / them" — the template serves speakers and headphones too). The invitation title
   *  and body that sat under it (2026-09-17 → 2026-10-17) are gone with the row's own ask: the
   *  one seated ask uses `tileHeadline` and the form's own question. */
  invite: {
    tag: 'You own this',
  },
  errors: {
    /** Declared, but nothing DealHunt could resolve: the deltas work, the note needs a page. */
    noPage:
      "We couldn't match that to a product page — the comparisons still work, the owner review needs a product page.",
    lookup: 'Could not look that product up — try again.',
  },
} as const

// part B — guide asks
/**
 * The best-list asks added by the owner-funnel release, part B (2026-10-07). Kept apart from
 * `GUIDE_COPY` so the two halves of the release merge cleanly.
 *
 *  - `roster`: the one seated ask on the pick the reader owns (its roster row, or its in-depth
 *    block when it has no row — 2026-10-17) asks the form's own buy-again question with its two
 *    answers — the tap is that answer and opens the band seeded with it, in place. A handed-off
 *    seat (NEXT_PUBLIC_OWNER_WRITER_HANDOFF) shows the single "Leave an owner review" link
 *    instead: a question over one link would not be answered. The body under the question
 *    ("Your answer lands right here…") is gone with the row-only ask; `FORM_COST_LINE` says
 *    the cost.
 *  - the chart line ("＋ Add the one you own to this chart", 2026-10-07 → 2026-10-17) is gone:
 *    the banner is the one place a guide asks what the reader owns.
 */
export const GUIDE_ASKS = {
  roster: {
    question: GUIDE_COPY.strip.question,
    answers: GUIDE_COPY.strip.answers,
  },
} as const

// slice D — the gate's notice on the category, guide and review-post asks
/**
 * The category, guide and review-post asks on a gated surface (`signInGateFor`, slice D,
 * 2026-10-17): each ungated twin above, plus `SIGN_IN_NEEDED` as its trailing sentence — told at
 * the ask, before any writing, inline or handed off (the community writer is gated too). Same
 * rules as `GATED_ASK_COPY`: reader-neutral, no method, safe in edge-cached HTML; no speed, no
 * moderator (aca4da79), no anonymity. The declare asks (the category "Own one already?", the
 * guide's chooser) print `SIGN_IN_NEEDED` as fine print beside their words;
 * the guide banner says it in its body and drops "Nothing to sign up for".
 */
export const GATED_GUIDE_COPY = {
  line: {
    body: `${GUIDE_COPY.line.body} ${SIGN_IN_NEEDED}`,
    /** The caption under "Pick what you own": declaring is still free of any sign-up. */
    note: 'Comparing needs no sign-up',
  },
  strip: {
    /** The category and guide reference strip's Yes / No body, either `cost` (inline or the
     *  handoff hint). */
    questionBody: (ownerCount: number, cost?: string): string =>
      `${GUIDE_COPY.strip.questionBody(ownerCount, cost)} ${SIGN_IN_NEEDED}`,
  },
} as const

/** `FORM_COST_LINE` on a gated surface (the guide's seated ask): the requirement before the
 *  photo, which stays the last, optional, word. */
export const GATED_FORM_COST_LINE =
  'Three questions, four quick reads, one sentence. Sign-in needed to publish. Photo optional.'

/**
 * The review post's asks (`review/ReviewOwnersCarousel` card, `review/ReviewOwnersStrip`): the
 * carousel card's sub-line and the strip's hint under its buttons, per tone. Neutral on purpose:
 * the ask never fishes for a side.
 */
export const REVIEW_ASK_COPY = {
  carouselSub: "Tell other readers how they've held up.",
  stripHint: {
    neutral: 'Own a pair? Help other buyers.',
    early: 'Every review counts while it is early.',
    up: 'Own a pair? Help other buyers.',
    mixed: 'Owners are split. Where do you land?',
    down: 'Had a better experience? Say so.',
  },
} as const

export const GATED_REVIEW_ASK_COPY = {
  carouselSub: `${REVIEW_ASK_COPY.carouselSub} ${SIGN_IN_NEEDED}`,
  stripHint: {
    neutral: `${REVIEW_ASK_COPY.stripHint.neutral} ${SIGN_IN_NEEDED}`,
    early: `${REVIEW_ASK_COPY.stripHint.early} ${SIGN_IN_NEEDED}`,
    up: `${REVIEW_ASK_COPY.stripHint.up} ${SIGN_IN_NEEDED}`,
    mixed: `${REVIEW_ASK_COPY.stripHint.mixed} ${SIGN_IN_NEEDED}`,
    down: `${REVIEW_ASK_COPY.stripHint.down} ${SIGN_IN_NEEDED}`,
  },
} as const
